import { useId } from "react";
import { RowLink, RowList } from "../../ui/RowLinks";
import { useClientPath } from "../../data/readSource";
import { ACCOUNT_ENTRIES, COMPANY_ENTRIES, type SettingsEntry, type SettingsSectionKey } from "./sections";

/**
 * The list of Inställningar, two boxes: the person's own account and the company's
 * (`prototype/kund/pages.js`, `settingsNav`).
 *
 * On a phone it is the whole of `/settings`; on a computer it stays on the left beside whichever
 * section is open. The company's group is titled with the company's own name, because "ni" on this
 * screen means them and the name says so better than a generic word. Without a tenant there is no
 * company to speak of, so the group is left out rather than titled with a placeholder.
 */
export function SettingsNav({
  active,
  tenantName
}: {
  active: SettingsSectionKey | null;
  tenantName: string | null;
}) {
  return (
    <nav className="k-set-groups" aria-label="Inställningar">
      <Group title="Ditt konto" entries={ACCOUNT_ENTRIES} active={active} />
      {tenantName ? <Group title={tenantName} entries={COMPANY_ENTRIES} active={active} /> : null}
    </nav>
  );
}

function Group({
  title,
  entries,
  active
}: {
  title: string;
  entries: SettingsEntry[];
  active: SettingsSectionKey | null;
}) {
  const titleId = useId();
  const clientPath = useClientPath();

  return (
    <section className="panel k-set-group" aria-labelledby={titleId}>
      <h2 className="k-set-title" id={titleId}>
        {title}
      </h2>
      <RowList compact>
        {entries.map((entry) => (
          <RowLink
            key={entry.key}
            to={clientPath(entry.to)}
            icon={entry.icon}
            current={entry.key === active}
            testId={`settings-link-${entry.key}`}
          >
            {entry.label}
          </RowLink>
        ))}
      </RowList>
    </section>
  );
}
